import { useEffect, useRef } from "react";
import { Star, ExternalLink } from "lucide-react";
import { testimonials } from "@/data/testimonials";
import { site } from "@/config/site";
import { Section } from "./Section";
import { Reveal } from "./Reveal";

const Stars = ({ n }) => (
    <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
        {[...Array(n)].map((_, i) => (
            <Star key={i} size={13} className="fill-rz-red text-rz-red" />
        ))}
    </div>
);

const Card = ({ t }) => (
    <article
        data-testid={`testimonial-card-${t.id}`}
        className="rz-corners flex w-[84vw] max-w-[440px] shrink-0 select-none flex-col border border-rz-steel/60 bg-rz-carbon p-7 sm:w-[440px]"
    >
        <Stars n={t.rating} />
        <p className="mt-5 flex-1 text-sm leading-relaxed text-rz-white/90">"{t.quote}"</p>
        <div className="mt-6 border-t border-rz-steel/60 pt-4">
            <p className="font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-white">
                {t.name}
            </p>
            <p className="mt-1.5 font-display text-[10px] uppercase tracking-[0.2em] text-rz-smoke">
                {t.vehicle} <span className="mx-1.5 text-rz-red">·</span> {t.service}
            </p>
        </div>
    </article>
);

export const Testimonials = () => {
    const trackRef = useRef(null);
    const pos = useRef(0);
    const hovering = useRef(false);
    const dragging = useRef(false);
    const lastX = useRef(0);

    const items = [...testimonials, ...testimonials];

    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let raf;
        let last = performance.now();
        const step = (now) => {
            const dt = Math.min((now - last) / 1000, 0.05);
            last = now;
            const track = trackRef.current;
            if (track) {
                const half = track.scrollWidth / 2;
                if (half > 0) {
                    if (!reduce && !hovering.current && !dragging.current) pos.current += 20 * dt;
                    pos.current = ((pos.current % half) + half) % half;
                    track.style.transform = `translateX(${-pos.current}px)`;
                }
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <Section id="reviews" index="02" label="Social Proof" title="What our customers say." alt>
            <Reveal className="mt-12" delay={0.05}>
                <div
                    data-testid="testimonial-carousel"
                    className="relative -mx-5 cursor-grab touch-pan-y overflow-hidden active:cursor-grabbing sm:-mx-8 lg:-mx-12"
                    onMouseEnter={() => (hovering.current = true)}
                    onMouseLeave={() => (hovering.current = false)}
                    onPointerDown={(e) => {
                        dragging.current = true;
                        lastX.current = e.clientX;
                        e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                        if (!dragging.current) return;
                        pos.current -= e.clientX - lastX.current;
                        lastX.current = e.clientX;
                    }}
                    onPointerUp={() => (dragging.current = false)}
                    onPointerCancel={() => (dragging.current = false)}
                >
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-rz-carbon to-transparent sm:w-20" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-rz-carbon to-transparent sm:w-20" />
                    <div ref={trackRef} className="flex w-max gap-6 will-change-transform">
                        {items.map((t, i) => (
                            <Card key={`${t.id}-${i}`} t={t} />
                        ))}
                    </div>
                </div>
            </Reveal>
            <Reveal className="mt-8" delay={0.1}>
                <p className="text-sm text-rz-smoke">
                    Genuine customer feedback, published with consent.{" "}
                    <a
                        data-testid="reviews-google-link"
                        href={site.googleProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-red transition-transform duration-300 hover:translate-x-1"
                    >
                        See our Google profile <ExternalLink size={12} />
                    </a>
                </p>
            </Reveal>
        </Section>
    );
};
